/**
 * useReportTags - Tag and AI-classification state for an admin report
 *
 * Extracted from useAdminReportData.js. Owns:
 * - KPI / HRO / Location tag state and trigger-based patching
 * - AI override/restore for KPI and HRO classifications
 *
 * @param {string} uuid - The report UUID
 * @param {number} timestamp - The report timestamp (sort key)
 * @param {Object} deps
 * @param {Function} deps.markReportChanged - Persist the changed-report flag
 */

import { useState, useEffect, useCallback } from 'react';

import Util from '../util/util';
import { showSuccess } from '../util/error/hookErrors';
import { hroCodesToLabels, hroLabelsToCodes } from '../util/constants';
import { trackEngagementAsync } from '../util/engagementTracker';
import { t } from '../i18n';

const useReportTags = (uuid, timestamp, { markReportChanged }) => {
  // Tags
  const [kpis, setKpis] = useState([]);
  const [hros, setHros] = useState([]);
  const [locations, setLocations] = useState([]);

  // AI fields - separate override state for KPIs and HROs
  const [aiKpiOverridden, setAiKpiOverridden] = useState(false);
  const [aiHroOverridden, setAiHroOverridden] = useState(false);
  const [aiKpis, setAiKpis] = useState([]);
  const [aiHros, setAiHros] = useState([]);
  // Legacy combined override (for backwards compatibility)
  const [aiOverridden, setAiOverridden] = useState(false);

  // Tag update triggers
  const [shouldUpdateKpis, setShouldUpdateKpis] = useState(false);
  const [shouldUpdateHros, setShouldUpdateHros] = useState(false);
  const [shouldUpdateLocations, setShouldUpdateLocations] = useState(false);

  /** Populate tag and AI state from a fetched report */
  const initializeTags = useCallback((report) => {
    // AI override states - support both new separate fields and legacy combined field
    const legacyOverride = report.AIOverride === true;
    setAiOverridden(legacyOverride);

    // Once either new field is explicitly set (true or false), we're in "new mode"
    // and should not use legacy fallback for either field
    const hasNewOverrideFields = report.AIOverrideKPI !== undefined || report.AIOverrideHRO !== undefined;
    if (hasNewOverrideFields) {
      // New mode: undefined means false, explicit values are used
      setAiKpiOverridden(report.AIOverrideKPI === true);
      setAiHroOverridden(report.AIOverrideHRO === true);
    } else {
      // Legacy mode: fall back to combined AIOverride field
      setAiKpiOverridden(legacyOverride);
      setAiHroOverridden(legacyOverride);
    }

    // KPIs
    if (Array.isArray(report.KPIs) && report.KPIs.length > 0) {
      setKpis(report.KPIs.sort());
    } else {
      setKpis([]);
    }

    // HROs (codes -> labels)
    if (Array.isArray(report.HROs) && report.HROs.length > 0) {
      setHros(hroCodesToLabels(report.HROs));
    } else {
      setHros([]);
    }

    // AI HROs
    if (report.HRO_AI) {
      setAiHros(hroCodesToLabels([report.HRO_AI]));
    } else {
      setAiHros([]);
    }

    // AI KPIs
    if (report.KPI_AI) {
      setAiKpis([report.KPI_AI]);
    } else {
      setAiKpis([]);
    }

    // Locations
    if (Array.isArray(report.locations)) {
      setLocations(report.locations);
    } else {
      setLocations([]);
    }
  }, []);

  /**
   * Patch tags on the report
   */
  const patchTags = useCallback(
    async (input, dataKey) => {
      await markReportChanged(true);

      const queryParams = {
        uuid: uuid,
        timestamp: timestamp,
      };
      queryParams[dataKey.toLowerCase()] = JSON.stringify(input);

      const requestData = { queryParams };

      try {
        const data = await Util.patch('Resty', `/items/object/${dataKey}`, requestData);
        showSuccess(t('reports:ticket.tagsUpdated'));

        // Track tag addition for surprise badges (shows toast if badge earned)
        trackEngagementAsync('tags_added');

        return data;
      } catch (err) {
        Util.printAPIError(err);
        return null;
      }
    },
    [uuid, timestamp, markReportChanged]
  );

  /**
   * Override AI classification for a specific type
   * @param {string} type - 'kpi', 'hro', or 'all'
   */
  const overrideAI = useCallback(async (type = 'all') => {
    const requestData = {
      queryParams: {
        uuid: uuid,
        timestamp: timestamp,
        reportId: Util.formatUuid(uuid),
        type: type,
      },
    };

    try {
      await Util.patch('Resty', '/items/override', requestData);
      await Util.setReportCache(uuid, 'reportChanged', true);

      // Update appropriate state based on type
      if (type === 'kpi') {
        setAiKpiOverridden(true);
        showSuccess(t('reports:ticket.aiKpiHidden'));
      } else if (type === 'hro') {
        setAiHroOverridden(true);
        showSuccess(t('reports:ticket.aiHroHidden'));
      } else {
        setAiOverridden(true);
        setAiKpiOverridden(true);
        setAiHroOverridden(true);
        showSuccess(t('reports:ticket.aiHidden'));
      }

      // Track AI override for surprise badges (shows toast if badge earned)
      trackEngagementAsync('ai_overridden');
    } catch (err) {
      Util.handleApiError(err, { userMessage: t('reports:ticket.aiOverrideFailed') });
    }
  }, [uuid, timestamp]);

  /**
   * Restore AI classification for a specific type (undo override)
   * @param {string} type - 'kpi', 'hro', or 'all'
   */
  const restoreAI = useCallback(async (type = 'all') => {
    const requestData = {
      queryParams: {
        uuid: uuid,
        timestamp: timestamp,
        reportId: Util.formatUuid(uuid),
        restore: 'true',
        type: type,
      },
    };

    try {
      await Util.patch('Resty', '/items/override', requestData);
      await Util.setReportCache(uuid, 'reportChanged', true);

      // Update appropriate state based on type
      if (type === 'kpi') {
        setAiKpiOverridden(false);
        showSuccess(t('reports:ticket.aiKpiRestored'));
      } else if (type === 'hro') {
        setAiHroOverridden(false);
        // Clear any manual HRO selections when restoring AI HRO
        setHros([]);
        setShouldUpdateHros(true);
        showSuccess(t('reports:ticket.aiHroRestored'));
      } else {
        setAiOverridden(false);
        setAiKpiOverridden(false);
        setAiHroOverridden(false);
        // Clear any manual HRO selections when restoring all AI suggestions
        setHros([]);
        setShouldUpdateHros(true);
        showSuccess(t('reports:ticket.aiRestored'));
      }
    } catch (err) {
      Util.handleApiError(err, { userMessage: t('reports:ticket.aiRestoreFailed') });
    }
  }, [uuid, timestamp]);

  // Tag update handlers
  const triggerKpisUpdate = useCallback(() => setShouldUpdateKpis(true), []);
  const triggerHrosUpdate = useCallback(() => setShouldUpdateHros(true), []);
  const triggerLocationsUpdate = useCallback(() => setShouldUpdateLocations(true), []);

  // Effect: Update KPIs when triggered
  useEffect(() => {
    if (shouldUpdateKpis) {
      patchTags(kpis, 'KPIs');
      setShouldUpdateKpis(false);
    }
  }, [kpis, shouldUpdateKpis, patchTags]);

  // Effect: Update HROs when triggered
  useEffect(() => {
    if (shouldUpdateHros) {
      const hroCodes = hroLabelsToCodes(hros);
      patchTags(hroCodes, 'HROs');
      setShouldUpdateHros(false);
    }
  }, [hros, shouldUpdateHros, patchTags]);

  // Effect: Update Locations when triggered
  useEffect(() => {
    if (shouldUpdateLocations) {
      patchTags(locations, 'Locations');
      setShouldUpdateLocations(false);
    }
  }, [locations, shouldUpdateLocations, patchTags]);

  return {
    kpis,
    setKpis,
    hros,
    setHros,
    locations,
    setLocations,
    aiOverridden,
    aiKpiOverridden,
    aiHroOverridden,
    aiKpis,
    aiHros,
    initializeTags,
    overrideAI,
    restoreAI,
    triggerKpisUpdate,
    triggerHrosUpdate,
    triggerLocationsUpdate,
  };
};

export default useReportTags;
